import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Services from '@/pages/Services';
import Industries from '@/pages/Industries';
import Contact from '@/pages/Contact';
import PowerPlants from '@/pages/sectors/PowerPlants';
import CoalFiredPowerPlants from '@/pages/sectors/CoalFiredPowerPlants';
import MiningConstruction from '@/pages/sectors/MiningConstruction';
import OilAndGas from '@/pages/sectors/OilAndGas';
import MilitaryInstallations from '@/pages/sectors/MilitaryInstallations';
import SeafoodProcessing from '@/pages/sectors/SeafoodProcessing';
import SoilRemediation from '@/pages/sectors/SoilRemediation';
import GeothermalPermitting from '@/pages/sectors/GeothermalPermitting';
import AntimonyRoasting from '@/pages/sectors/AntimonyRoasting';
import PowerPlantsContact from '@/pages/contact/PowerPlantsContact';
import OilAndGasContact from '@/pages/contact/OilAndGasContact';
import GeothermalContact from '@/pages/contact/GeothermalContact';
import MiningConstructionContact from '@/pages/contact/MiningConstructionContact';
import MilitaryContact from '@/pages/contact/MilitaryContact';
import SeafoodProcessingContact from '@/pages/contact/SeafoodProcessingContact';
import SoilRemediationContact from '@/pages/contact/SoilRemediationContact';
import CoalFiredContact from '@/pages/contact/CoalFiredContact';
import AntimonyRoastingContact from '@/pages/contact/AntimonyRoastingContact';
import PermitApplications from '@/pages/PermitApplications';
import PermitFeasibility from '@/pages/PermitFeasibility';
import EquipmentSelection from '@/pages/EquipmentSelection';
import PollutionControl from '@/pages/PollutionControl';
import ComplianceMonitoring from '@/pages/ComplianceMonitoring';
import RegulatorySupport from '@/pages/RegulatorySupport';
import ModelingTechnicalSupport from '@/pages/ModelingTechnicalSupport';
import CostEffectiveSolutions from '@/pages/CostEffectiveSolutions';
import PermitApplicationsService from '@/pages/services/PermitApplicationsService';
import ComplianceSupportService from '@/pages/services/ComplianceSupportService';
import RegulatoryGuidanceService from '@/pages/services/RegulatoryGuidanceService';
import CostEffectiveSolutionsService from '@/pages/services/CostEffectiveSolutionsService';
import Admin from '@/pages/Admin';
import AdminLogin from '@/pages/AdminLogin';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import NotFound from '@/pages/NotFound';
import '@/App.css';

// Old SEO Component (DEPRECATED - Now using centralized SEO from @/components/SEO)
// This component now returns null to avoid conflicts with the new centralized SEO
const OldSEO = ({ title, description, keywords, type = 'website', structuredData = null }) => {
  // Return null - centralized SEO component handles all pages now
  return null;
  
  // Old implementation commented out to prevent conflicts
  /*
  useEffect(() => {
    // Set page title
    document.title = title || 'Compliance Risk Estimator | Raven Environmental';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description || 'Instant compliance risk estimation for EPA/ADEC violations. Protect your operations with proactive auditing.');
    }

    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords || 'environmental consulting, Alaska, air quality permits, environmental compliance, regulatory compliance, environmental permits, Alaska environmental services');
    }

    // Set Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title || 'Raven Environmental Consulting, LLC');

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.setAttribute('content', description || 'Expert environmental consulting services in Alaska');

    let ogType = document.querySelector('meta[property="og:type"]');
    if (!ogType) {
      ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      document.head.appendChild(ogType);
    }
    ogType.setAttribute('content', type);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', window.location.href);

    // Twitter Card tags
    let twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (!twitterCard) {
      twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      document.head.appendChild(twitterCard);
    }
    twitterCard.setAttribute('content', 'summary_large_image');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);

    // Add structured data if provided
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, type, structuredData]);

  return null;
  */
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App min-h-screen flex flex-col">
        <BrowserRouter>
          <SEO />
          <ScrollToTop />
          <Navbar />
          <main className="flex-grow">
            <Routes>
            <Route
              path="/"
              element={
                <>
                  
                  <Home />
                </>
              }
            />
            
            {/* Redirect /home to / for SEO and broken link fix */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            
            <Route
              path="/about"
              element={
                <>
                  
                  <About />
                </>
              }
            />
            <Route
              path="/services"
              element={
                <>
                  
                  <Services />
                </>
              }
            />
            <Route
              path="/industries"
              element={
                <>
                  
                  <Industries />
                </>
              }
            />
            <Route
              path="/permit-applications"
              element={
                <>
                  
                  <PermitApplications />
                </>
              }
            />
            <Route
              path="/permit-feasibility"
              element={
                <>
                  
                  <PermitFeasibility />
                </>
              }
            />
            <Route
              path="/equipment-selection"
              element={
                <>
                  
                  <EquipmentSelection />
                </>
              }
            />
            <Route
              path="/pollution-control"
              element={
                <>
                  
                  <PollutionControl />
                </>
              }
            />
            <Route
              path="/compliance-monitoring"
              element={
                <>
                  
                  <ComplianceMonitoring />
                </>
              }
            />
            <Route
              path="/regulatory-support"
              element={
                <>
                  
                  <RegulatorySupport />
                </>
              }
            />
            <Route
              path="/modeling-technical-support"
              element={
                <>
                  
                  <ModelingTechnicalSupport />
                </>
              }
            />
            <Route
              path="/cost-effective-inquiry"
              element={
                <>
                  
                  <CostEffectiveSolutions />
                </>
              }
            />
            <Route
              path="/services/permit-applications"
              element={
                <>
                  
                  <PermitApplicationsService />
                </>
              }
            />
            <Route
              path="/services/compliance-support"
              element={
                <>
                  
                  <ComplianceSupportService />
                </>
              }
            />
            <Route
              path="/services/regulatory-guidance"
              element={
                <>
                  
                  <RegulatoryGuidanceService />
                </>
              }
            />
            <Route
              path="/services/cost-effective-solutions"
              element={
                <>
                  
                  <CostEffectiveSolutionsService />
                </>
              }
            />
            <Route
              path="/contact"
              element={
                <>
                  
                  <Contact />
                </>
              }
            />
            <Route
              path="/sectors/power-plants"
              element={
                <>
                  
                  <PowerPlants />
                </>
              }
            />
            <Route
              path="/sectors/coal-fired-power-plants"
              element={
                <>
                  
                  <CoalFiredPowerPlants />
                </>
              }
            />
            <Route
              path="/sectors/mining-construction"
              element={
                <>
                  
                  <MiningConstruction />
                </>
              }
            />
            <Route
              path="/sectors/oil-and-gas"
              element={
                <>
                  
                  <OilAndGas />
                </>
              }
            />
            <Route
              path="/sectors/military-installations"
              element={
                <>
                  
                  <MilitaryInstallations />
                </>
              }
            />
            <Route
              path="/sectors/seafood-processing"
              element={
                <>
                  
                  <SeafoodProcessing />
                </>
              }
            />
            <Route
              path="/sectors/soil-remediation"
              element={
                <>
                  
                  <SoilRemediation />
                </>
              }
            />
            <Route
              path="/sectors/soil-remediation/contact"
              element={
                <>
                  
                  <SoilRemediationContact />
                </>
              }
            />
            <Route
              path="/sectors/geothermal-permitting"
              element={
                <>
                  
                  <GeothermalPermitting />
                </>
              }
            />
            <Route
              path="/sectors/antimony-roasting"
              element={
                <>
                  
                  <AntimonyRoasting />
                </>
              }
            />
            <Route
              path="/contact/power-plants"
              element={
                <>
                  
                  <PowerPlantsContact />
                </>
              }
            />
            <Route
              path="/contact/oil-and-gas"
              element={
                <>
                  
                  <OilAndGasContact />
                </>
              }
            />
            <Route
              path="/contact/geothermal"
              element={
                <>
                  
                  <GeothermalContact />
                </>
              }
            />
            <Route
              path="/contact/mining-construction"
              element={
                <>
                  
                  <MiningConstructionContact />
                </>
              }
            />
            <Route
              path="/contact/military"
              element={
                <>
                  
                  <MilitaryContact />
                </>
              }
            />
            <Route
              path="/contact/seafood-processing"
              element={
                <>
                  
                  <SeafoodProcessingContact />
                </>
              }
            />
            <Route
              path="/contact/soil-remediation"
              element={<Navigate to="/permit-feasibility" replace />}
            />
            <Route
              path="/contact/coal-fired"
              element={
                <>
                  
                  <CoalFiredContact />
                </>
              }
            />
            <Route
              path="/contact/antimony-roasting"
              element={
                <>
                  
                  <AntimonyRoastingContact />
                </>
              }
            />
            <Route
              path="/admin/login"
              element={
                <>
                  
                  <AdminLogin />
                </>
              }
            />
            <Route
              path="/admin"
              element={
                <>
                  
                  <Admin />
                </>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <>
                  
                  <PrivacyPolicy />
                </>
              }
            />
            <Route
              path="/terms-of-service"
              element={
                <>
                  
                  <TermsOfService />
                </>
              }
            />
            
            {/* 404 Catch-All Route - Must be last */}
            <Route
              path="*"
              element={<NotFound />}
            />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;